import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
  Container,
  Heading,
  Text,
  Link,
  HStack,
  InputGroup,
  InputRightElement,
  IconButton,
  useColorModeValue,
  Card,
  CardBody,
  Divider,
  Alert,
  AlertIcon,
  Progress,
  Badge,
  Textarea,
  Select,
  Checkbox,
  SimpleGrid,
  Step,
  StepDescription,
  StepIcon,
  StepIndicator,
  StepNumber,
  StepSeparator,
  StepStatus,
  StepTitle,
  Stepper,
  useSteps,
  CheckboxGroup,
  Wrap,
  WrapItem
} from '@chakra-ui/react'
import { useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { FiEye, FiEyeOff, FiMail, FiLock, FiUser, FiBuilding, FiCheck } from 'react-icons/fi'
import { useAuth } from '../../../contexts/AuthContext'
import apiClient from '../../../utils/apiClient'

const SignUp = () => {
  const navigate = useNavigate()
  const { login } = useAuth()
  const toast = useToast()

  // Color mode values
  const bg = useColorModeValue('gray.50', 'gray.900')
  const cardBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.600')

  // Multi-step form state
  const steps = [
    { title: 'Account', description: 'Personal Information' },
    { title: 'Organization', description: 'Company Details' },
    { title: 'Preferences', description: 'Setup Options' },
    { title: 'Review', description: 'Confirm Details' }
  ]

  const { activeStep, setActiveStep } = useSteps({
    index: 0,
    count: steps.length,
  })

  // Form data state
  const [formData, setFormData] = useState({
    // Step 1: Account Info
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    
    // Step 2: Organization Info
    organizationName: '',
    organizationType: '',
    industry: '',
    website: '',
    description: '',
    
    // Step 3: Preferences
    timezone: 'UTC',
    features: [],
    agreeToTerms: false,
    subscribeToUpdates: true
  })

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [checkingEmail, setCheckingEmail] = useState(false)

  // Available options
  const organizationTypes = [
    'Startup',
    'Small Business',
    'Enterprise',
    'Non-Profit',
    'Government',
    'Educational',
    'Personal'
  ]

  const industries = [
    'Technology',
    'Healthcare',
    'Finance',
    'Education',
    'E-commerce',
    'Media & Entertainment',
    'Real Estate',
    'Manufacturing',
    'Other'
  ]

  const availableFeatures = [
    'Multi-language Support',
    'Advanced SEO Tools',
    'E-commerce Integration',
    'Custom Workflows',
    'API Access',
    'Analytics Dashboard'
  ]

  const timezones = [
    'UTC',
    'America/New_York',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Asia/Tokyo',
    'Australia/Sydney'
  ]

  // Validation functions
  const validateStep = (step) => {
    const newErrors = {}

    switch (step) {
      case 0: // Account Info
        if (!formData.firstName.trim()) newErrors.firstName = 'First name is required'
        if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required'
        if (!formData.email.trim()) {
          newErrors.email = 'Email is required'
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
          newErrors.email = 'Please enter a valid email address'
        }
        if (!formData.password) {
          newErrors.password = 'Password is required'
        } else if (formData.password.length < 8) {
          newErrors.password = 'Password must be at least 8 characters'
        } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
          newErrors.password = 'Password must contain uppercase, lowercase, and number'
        }
        if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = 'Passwords do not match'
        }
        break

      case 1: // Organization Info
        if (!formData.organizationName.trim()) newErrors.organizationName = 'Organization name is required'
        if (!formData.organizationType) newErrors.organizationType = 'Organization type is required'
        if (!formData.industry) newErrors.industry = 'Industry is required'
        if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
          newErrors.website = 'Please enter a valid URL (including http:// or https://)'
        }
        break

      case 2: // Preferences
        if (!formData.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms of service'
        break
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Check if email exists (you'll need to add this endpoint to your API)
  const checkEmailExists = async (email) => {
    if (!email || !/\S+@\S+\.\S+/.test(email)) return false
    
    setCheckingEmail(true)
    try {
      // FIXED: Add / prefix
      const response = await apiClient('/users/check-email', {
        method: 'POST',
        body: JSON.stringify({ email })
      })
      return response.exists
    } catch (error) {
      console.error('Error checking email:', error)
      return false
    } finally {
      setCheckingEmail(false)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleNext = async () => {
    if (!validateStep(activeStep)) return

    // Check email on first step
    if (activeStep === 0) {
      const emailExists = await checkEmailExists(formData.email)
      if (emailExists) {
        setErrors({ email: 'An account with this email already exists' })
        return
      }
    }

    setActiveStep(activeStep + 1)
  }

  const handlePrevious = () => {
    setActiveStep(activeStep - 1)
  }

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) return

    setLoading(true)
    try {
      console.log('Starting signup process...')

      // Step 1: Register user first - FIXED: Add / prefix
      const userResponse = await apiClient('/users/register', {
        method: 'POST',
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          password: formData.password
        })
      })

      console.log('User registration response:', userResponse)

      if (!userResponse || !userResponse.user_id) {
        throw new Error('Failed to create user account')
      }

      const userId = userResponse.user_id

      // Step 2: Create tenant (organization) - FIXED: Add / prefix
      const tenantResponse = await apiClient('/tenants', {
        method: 'POST',
        body: JSON.stringify({
          name: formData.organizationName,
          type: formData.organizationType,
          industry: formData.industry,
          website: formData.website || '',
          description: formData.description || '',
          settings: JSON.stringify({
            timezone: formData.timezone,
            features: formData.features,
            subscribeToUpdates: formData.subscribeToUpdates
          })
        })
      })

      console.log('Tenant creation response:', tenantResponse)

      if (!tenantResponse || !tenantResponse.id) {
        throw new Error('Failed to create organization')
      }

      const tenantId = tenantResponse.id

      // Step 3: Assign user to tenant - FIXED: Add / prefix
      const assignUserResponse = await apiClient('/tenants/assign-user', {
        method: 'POST',
        body: JSON.stringify({
          user_id: userId,
          tenant_id: tenantId
        })
      })

      console.log('User assigned to tenant successfully:', assignUserResponse)

      // Step 4: Assign admin role to user - FIXED: Add / prefix
      try {
        const roleResponse = await apiClient(`/users/${userId}/assign-role`, {
          method: 'POST',
          body: JSON.stringify({
            role_id: '1',
            tenant_id: tenantId
          })
        })
        console.log('Admin role assigned successfully:', roleResponse)
      } catch (roleError) {
        console.warn('Role assignment failed, but continuing with signup:', roleError)
      }

      // Step 5: Auto-login the user
      try {
        await login(formData.email, formData.password)
        
        toast({
          title: 'Account created successfully!',
          description: 'Welcome to CMS Dashboard. You have been automatically signed in.',
          status: 'success',
          duration: 5000,
        })

        navigate('/')
      } catch (loginError) {
        console.error('Auto-login failed:', loginError)
        
        toast({
          title: 'Account created successfully!',
          description: 'Please login with your credentials.',
          status: 'success',
          duration: 5000,
        })

        navigate('/login')
      }

    } catch (error) {
      console.error('Signup error:', error)
      
      let errorMessage = 'There was an error creating your account. Please try again.'
      
      if (error.message) {
        errorMessage = error.message
      } else if (error.response && error.response.message) {
        errorMessage = error.response.message
      } else if (typeof error === 'string') {
        errorMessage = error
      }

      toast({
        title: 'Account Creation Failed',
        description: errorMessage,
        status: 'error',
        duration: 5000,
      })
    } finally {
      setLoading(false)
    }
  }

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <VStack spacing={4} align="stretch">
            <Heading size="md" mb={2}>Create Your Account</Heading>
            <Text color="gray.500" mb={4}>
              Let's start with your personal information
            </Text>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isRequired isInvalid={!!errors.firstName}>
                <FormLabel>First Name</FormLabel>
                <Input
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  placeholder="Enter your first name"
                />
                {errors.firstName && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {errors.firstName}
                  </Text>
                )}
              </FormControl>

              <FormControl isRequired isInvalid={!!errors.lastName}>
                <FormLabel>Last Name</FormLabel>
                <Input
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  placeholder="Enter your last name"
                />
                {errors.lastName && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {errors.lastName}
                  </Text>
                )}
              </FormControl>
            </SimpleGrid>

            <FormControl isRequired isInvalid={!!errors.email}>
              <FormLabel>Email Address</FormLabel>
              <InputGroup>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Enter your email address"
                />
                <InputRightElement>
                  {checkingEmail ? (
                    <Box animation="spin 2s linear infinite">⟳</Box>
                  ) : (
                    <FiMail color="gray.400" />
                  )}
                </InputRightElement>
              </InputGroup>
              {errors.email && (
                <Text color="red.500" fontSize="sm" mt={1}>
                  {errors.email}
                </Text>
              )}
            </FormControl>

            <FormControl isRequired isInvalid={!!errors.password}>
              <FormLabel>Password</FormLabel>
              <InputGroup>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="Create a strong password"
                />
                <InputRightElement>
                  <IconButton
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPassword(!showPassword)}
                    icon={showPassword ? <FiEyeOff /> : <FiEye />}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  />
                </InputRightElement>
              </InputGroup>
              {errors.password && (
                <Text color="red.500" fontSize="sm" mt={1}>
                  {errors.password}
                </Text>
              )}
              <Text fontSize="xs" color="gray.500" mt={1}>
                Password must be at least 8 characters with uppercase, lowercase, and number
              </Text>
            </FormControl>

            <FormControl isRequired isInvalid={!!errors.confirmPassword}>
              <FormLabel>Confirm Password</FormLabel>
              <InputGroup>
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  placeholder="Confirm your password"
                />
                <InputRightElement>
                  <IconButton
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    icon={showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  />
                </InputRightElement>
              </InputGroup>
              {errors.confirmPassword && (
                <Text color="red.500" fontSize="sm" mt={1}>
                  {errors.confirmPassword}
                </Text>
              )}
            </FormControl>
          </VStack>
        )

      case 1:
        return (
          <VStack spacing={4} align="stretch">
            <Heading size="md" mb={2}>Organization Details</Heading>
            <Text color="gray.500" mb={4}>
              Tell us about your organization
            </Text>

            <FormControl isRequired isInvalid={!!errors.organizationName}>
              <FormLabel>Organization Name</FormLabel>
              <Input
                value={formData.organizationName}
                onChange={(e) => handleInputChange('organizationName', e.target.value)}
                placeholder="Enter your organization name"
              />
              {errors.organizationName && (
                <Text color="red.500" fontSize="sm" mt={1}>
                  {errors.organizationName}
                </Text>
              )}
            </FormControl>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isRequired isInvalid={!!errors.organizationType}>
                <FormLabel>Organization Type</FormLabel>
                <Select
                  value={formData.organizationType}
                  onChange={(e) => handleInputChange('organizationType', e.target.value)}
                  placeholder="Select organization type"
                >
                  {organizationTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </Select>
                {errors.organizationType && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {errors.organizationType}
                  </Text>
                )}
              </FormControl>

              <FormControl isRequired isInvalid={!!errors.industry}>
                <FormLabel>Industry</FormLabel>
                <Select
                  value={formData.industry}
                  onChange={(e) => handleInputChange('industry', e.target.value)}
                  placeholder="Select industry"
                >
                  {industries.map(industry => (
                    <option key={industry} value={industry}>{industry}</option>
                  ))}
                </Select>
                {errors.industry && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {errors.industry}
                  </Text>
                )}
              </FormControl>
            </SimpleGrid>

            <FormControl isInvalid={!!errors.website}>
              <FormLabel>Website (Optional)</FormLabel>
              <Input
                value={formData.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
                placeholder="https://yourwebsite.com"
              />
              {errors.website && (
                <Text color="red.500" fontSize="sm" mt={1}>
                  {errors.website}
                </Text>
              )}
            </FormControl>

            <FormControl>
              <FormLabel>Description (Optional)</FormLabel>
              <Textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Brief description of your organization"
                rows={3}
              />
            </FormControl>
          </VStack>
        )

      case 2:
        return (
          <VStack spacing={6} align="stretch">
            <Heading size="md" mb={2}>Setup Preferences</Heading>
            <Text color="gray.500" mb={4}>
              Customize your experience
            </Text>

            <FormControl>
              <FormLabel>Timezone</FormLabel>
              <Select
                value={formData.timezone}
                onChange={(e) => handleInputChange('timezone', e.target.value)}
              >
                {timezones.map(tz => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Features (Optional)</FormLabel>
              <Text fontSize="sm" color="gray.500" mb={3}>
                Select features you're interested in:
              </Text>
              <CheckboxGroup
                value={formData.features}
                onChange={(values) => handleInputChange('features', values)}
              >
                <Wrap spacing={3}>
                  {availableFeatures.map(feature => (
                    <WrapItem key={feature}>
                      <Checkbox value={feature} colorScheme="blue">
                        <Text fontSize="sm">{feature}</Text>
                      </Checkbox>
                    </WrapItem>
                  ))}
                </Wrap>
              </CheckboxGroup>
            </FormControl>

            <Divider />

            <VStack spacing={3} align="stretch">
              <FormControl isRequired isInvalid={!!errors.agreeToTerms}>
                <Checkbox
                  isChecked={formData.agreeToTerms}
                  onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
                  colorScheme="blue"
                >
                  <Text fontSize="sm">
                    I agree to the{' '}
                    <Link color="blue.500" href="#" isExternal>
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link color="blue.500" href="#" isExternal>
                      Privacy Policy
                    </Link>
                  </Text>
                </Checkbox>
                {errors.agreeToTerms && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {errors.agreeToTerms}
                  </Text>
                )}
              </FormControl>

              <Checkbox
                isChecked={formData.subscribeToUpdates}
                onChange={(e) => handleInputChange('subscribeToUpdates', e.target.checked)}
                colorScheme="blue"
              >
                <Text fontSize="sm">
                  Subscribe to product updates and newsletters
                </Text>
              </Checkbox>
            </VStack>
          </VStack>
        )

      case 3:
        return (
          <VStack spacing={6} align="stretch">
            <Heading size="md" mb={2}>Review Your Information</Heading>
            <Text color="gray.500" mb={4}>
              Please review your details before creating your account
            </Text>

            <VStack spacing={4} align="stretch">
              <Box p={4} borderWidth={1} borderColor={borderColor} borderRadius="md">
                <Text fontWeight="bold" mb={2}>Account Information</Text>
                <Text fontSize="sm" color="gray.600">
                  Name: {formData.firstName} {formData.lastName}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Email: {formData.email}
                </Text>
              </Box>

              <Box p={4} borderWidth={1} borderColor={borderColor} borderRadius="md">
                <Text fontWeight="bold" mb={2}>Organization Details</Text>
                <Text fontSize="sm" color="gray.600">
                  Name: {formData.organizationName}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Type: {formData.organizationType}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Industry: {formData.industry}
                </Text>
                {formData.website && (
                  <Text fontSize="sm" color="gray.600">
                    Website: {formData.website}
                  </Text>
                )}
              </Box>

              <Box p={4} borderWidth={1} borderColor={borderColor} borderRadius="md">
                <Text fontWeight="bold" mb={2}>Preferences</Text>
                <Text fontSize="sm" color="gray.600">
                  Timezone: {formData.timezone}
                </Text>
                {formData.features.length > 0 && (
                  <Text fontSize="sm" color="gray.600">
                    Selected Features: {formData.features.join(', ')}
                  </Text>
                )}
              </Box>
            </VStack>

            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">
                You will be automatically assigned as an administrator for your organization.
              </Text>
            </Alert>
          </VStack>
        )

      default:
        return null
    }
  }

  return (
    <Box minH="100vh" bg={bg} py={8}>
      <Container maxW="container.xl" px={4}>
        <VStack spacing={8} align="stretch">
          {/* Header */}
          <Box textAlign="center">
            <Heading size="2xl" mb={2} bgGradient="linear(to-r, blue.400, purple.500)" bgClip="text">
              Join CMS Dashboard
            </Heading>
            <Text color="gray.500" fontSize="lg">
              Create your account and start managing content today
            </Text>
          </Box>

          {/* Progress Stepper */}
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody p={6}>
              <Stepper index={activeStep} colorScheme="blue">
                {steps.map((step, index) => (
                  <Step key={index}>
                    <StepIndicator>
                      <StepStatus
                        complete={<StepIcon />}
                        incomplete={<StepNumber />}
                        active={<StepNumber />}
                      />
                    </StepIndicator>

                    <Box flexShrink="0">
                      <StepTitle>{step.title}</StepTitle>
                      <StepDescription>{step.description}</StepDescription>
                    </Box>

                    <StepSeparator />
                  </Step>
                ))}
              </Stepper>
            </CardBody>
          </Card>

          {/* Form Card */}
          <Card bg={cardBg} borderColor={borderColor} variant="outline" shadow="xl">
            <CardBody p={8}>
              {renderStepContent()}

              {/* Navigation Buttons */}
              <HStack justify="space-between" mt={8}>
                <Button
                  variant="ghost"
                  onClick={handlePrevious}
                  isDisabled={activeStep === 0}
                >
                  Previous
                </Button>

                <HStack>
                  {activeStep < steps.length - 1 ? (
                    <Button
                      colorScheme="blue"
                      onClick={handleNext}
                      isLoading={checkingEmail && activeStep === 0}
                      loadingText="Checking..."
                    >
                      Next
                    </Button>
                  ) : (
                    <Button
                      colorScheme="green"
                      onClick={handleSubmit}
                      isLoading={loading}
                      loadingText="Creating Account..."
                      leftIcon={<FiCheck />}
                    >
                      Create Account
                    </Button>
                  )}
                </HStack>
              </HStack>
            </CardBody>
          </Card>

          {/* Sign In Link */}
          <Text textAlign="center" color="gray.500">
            Already have an account?{' '}
            <Link
              as={RouterLink}
              to="/login"
              color="blue.500"
              _hover={{ textDecoration: 'underline' }}
            >
              Sign in here
            </Link>
          </Text>
        </VStack>
      </Container>
    </Box>
  )
}

export default SignUp